import { apiClient } from '$lib/api/client';
import { queryFilters } from '$lib/stores/selection';
import type { Filters } from '$lib/types/api';
import { selectedIndustryCentroids, industryCentroids } from '$lib/stores/data';
import { loadPublicIndustryCentroids } from '$lib/api/data';
import { get } from 'svelte/store';
import type { AssistantState, AssistantResponse } from '$lib/types/ai';
import { guardToken } from '$lib/turnstile';

export async function submitAssistantMessage(state: AssistantState): Promise<AssistantResponse> {
  const selected = get(selectedIndustryCentroids);

  // /deep-iq/assistant is guarded — it runs an LLM call on an unauthenticated
  // POST, which is the whole reason the guard is there. This was the one
  // guarded caller written without a token, so the assistant answered every
  // message with the API's refusal.
  const turnstileToken = await guardToken();

  const response = await apiClient.post<AssistantResponse>('/deep-iq/assistant', {
    messages: state.messages,
    filters: state.filters,
    industry_queries: state.industry_queries,
    refinement_query: state.refinement_query,
    // The names, not the ids.
    //
    // This used to send `data_pool_ids`, which the handler has no field for —
    // so the assistant was told nothing about the industries the customer had
    // picked, and "add companies doing AI" read as "search for companies doing
    // AI". The model reasons about these in prose; a list of centroid numbers
    // would be no more use to it than an empty request was.
    selected_industries: Array.from(selected.values()),
    turnstile_token: turnstileToken
  });

  return response.data;
}

export function applyAIFilters(aiFilters: Partial<Filters>, refinementQuery?: string) {
  // Helper: only use AI value for arrays if non-empty, otherwise keep current
  const arr = <T>(aiVal: T[] | undefined, curVal: T[]) =>
    Array.isArray(aiVal) && aiVal.length > 0 ? aiVal : curVal;

  queryFilters.update((current) => ({
    ...current,
    cantons: arr(aiFilters.cantons, current.cantons),
    languages: arr(aiFilters.languages, current.languages),
    company_size_range: aiFilters.company_size_range?.some((v: number | null) => v !== null)
      ? aiFilters.company_size_range
      : current.company_size_range,
    founding_year_range: aiFilters.founding_year_range?.some((v: number | null) => v !== null)
      ? aiFilters.founding_year_range
      : current.founding_year_range,
    contact_roles: arr(aiFilters.contact_roles, current.contact_roles),
    // The assistant resolves a phrase into titles server-side and returns them
    // here; without this they were dropped on the way into the filter store.
    contact_titles: arr(aiFilters.contact_titles, current.contact_titles ?? []),
    postal_codes: arr(aiFilters.postal_codes, current.postal_codes),
    postal_radius: aiFilters.postal_radius?.center_postal_code
      ? aiFilters.postal_radius
      : current.postal_radius,
    // Only use refinement_query for embedding_query, never industry queries
    embedding_query: refinementQuery || '',
    text_constraints: arr(aiFilters.text_constraints, current.text_constraints),
    // Overlay only the keys the model actually set. The previous form built
    // the object with Object.fromEntries and then re-read the current value
    // for anything null — the same result, but through a Record<string, any>
    // that no longer resembled the filter shape it was assigned to.
    structured_filters: {
      ...current.structured_filters,
      ...Object.fromEntries(
        Object.entries(aiFilters.structured_filters ?? {}).filter(
          ([, value]) => value !== null && value !== undefined
        )
      )
    }
  }));
}

/**
 * Fold the industries the assistant matched into the customer's selection.
 *
 * # Why this adds rather than replaces
 *
 * It replaced, and that is half of why refining did not work: with software
 * companies selected, "companies doing AI" returned a match for AI and this
 * function threw software away. The customer asked for more and got fewer.
 *
 * Adding is the default because refining a selection almost always means
 * widening or narrowing it, not starting again — and the mistake it can make is
 * the recoverable one. A selection left too broad is visible in the count and
 * removable by hand; industries silently deleted are work the customer has to
 * notice is missing before they can redo it.
 *
 * `replace` is the assistant's to ask for, and it asks only when the customer
 * has clearly said so ("nur noch KI-Firmen").
 */
export async function applyAIIndustryMatches(
  matches: { centroid_id: number; centroid_name: string; matched: boolean }[],
  replace = false
) {
  if (!matches || matches.length === 0) return;

  const matched = matches.filter((m) => m.matched);
  if (matched.length === 0) return;

  // Ensure centroids are loaded so DataSelection UI stays consistent
  let allCentroids = get(industryCentroids);
  if (allCentroids.length === 0) {
    await loadPublicIndustryCentroids();
    allCentroids = get(industryCentroids);
  }

  const updated = replace ? new Map() : new Map(get(selectedIndustryCentroids));
  for (const match of matched) {
    const id = String(match.centroid_id);
    const centroid = allCentroids.find((c) => c.id === match.centroid_id);
    const name = match.centroid_name || centroid?.name || `Pool #${match.centroid_id}`;
    updated.set(id, name);
  }
  selectedIndustryCentroids.set(updated);
}
